import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import { type IAuthData, type IFileSelectProps, type IFileRow } from "../../../utils/interfaces";
import useApi from "../../../utils/useApi";

const useStyles = makeStyles()((theme) => ({
  fileSelect: {
    padding: theme.spacing(2),
    width: "100%",
  },
  table: {
    minWidth: 650,
    [theme.breakpoints.down("md")]: {
      minWidth: 300, // Adjusts table for smaller screens
    },
  },
  headCell: {
    backgroundColor: theme.palette.violet.main,
    color: "white",
  },
  bodyCell: {
    fontSize: 14,
  },
  evenRow: {
    backgroundColor: theme.palette.violet.mainAlpha,
    "&:hover": {
      // Apply the hover effect only when hovering
      backgroundColor: "white", //theme.palette.blue.hoverAlpha,
    },
  },
  oddRow: {
    backgroundColor: theme.palette.blue.mainAlpha,
    "&:hover": {
      // Apply the hover effect only when hovering
      backgroundColor: "white", //theme.palette.blue.hoverAlpha,
    },
  },
  desktopGrid: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "block",
    }, 
  },
  mobileGrid: {
    display: "block",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
}));

const FileSelect = ({ apiBasePath, handleRowSelection } : IFileSelectProps) => {
  const { classes } = useStyles();
  const customFetch = useApi();
  const loaderData = useRouteLoaderData("root") as IAuthData;
  const token = loaderData.token;
  const { t } = useTranslation();
  const [data, setData] = useState<IFileRow[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (event : React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage : number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when rows per page change
  };

  const rowsToDisplay = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        token: token,
      };
      const body = JSON.stringify(payload);

      try {
        const fileData = await customFetch(
          apiBasePath + `/api/get_files_for_user`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: body,
          }
        );

        setData(fileData);
      } catch (error) {
        console.log("Error fetching files:", error);
      }
    };

    fetchData();
    //eslint-disable-next-line
  }, [apiBasePath, token]);

  const truncateString = (str: string, num: number) => {
    if (
      str == null ||
      str === "" ||
      str === undefined ||
      num == null ||
      num === 0
    ) {
      return "";
    }
    if (str.length <= num) {
      return str;
    }
    return str.slice(0, num) + "...";
  };

  const formatDate = (dateString: string) => {
    if (dateString === "" || dateString == null) {
      return "";
    }

    const date = new Date(dateString);
    return (
      date.toLocaleDateString("de-DE") + " " + date.toLocaleTimeString("de-DE")
    );
  };

  const desktopGrid = (
    <TableContainer className={classes.desktopGrid} component={Paper}>
        <Table className={classes.table} aria-label="Files">
          <TableHead>
            <TableRow>
              {["name", "fileType", "status", "time"].map(
                (header) => (
                  <TableCell key={header} className={classes.headCell}>
                    {t(header)}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsToDisplay.map((row, index) => (
              <TableRow
                key={row.id}
                onClick={() => handleRowSelection(row)}
                className={`${
                  index % 2 === 0 ? classes.evenRow : classes.oddRow
                }`}
              >
                <TableCell className={classes.bodyCell}>{row.name}</TableCell>
                <TableCell className={classes.bodyCell}>{row.type}</TableCell>
                <TableCell className={classes.bodyCell}>{row.status}</TableCell>
                <TableCell className={classes.bodyCell}>
                {formatDate(row.uploadTime)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
  );

  const mobileGrid = (
    <TableContainer className={classes.mobileGrid} component={Paper}>
        <Table className={classes.table} aria-label="Files">
          <TableHead>
            <TableRow>
              {[ "name","time"].map(
                (header) => (
                  <TableCell key={header} className={classes.headCell}>
                    {t(header)}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsToDisplay.map((row, index) => (
              <TableRow
                key={row.id}
                onClick={() => handleRowSelection(row)}
                className={`${
                  index % 2 === 0 ? classes.evenRow : classes.oddRow
                }`}
              >
                <TableCell className={classes.bodyCell}>
                  {truncateString(row.name, 30)}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {formatDate(row.uploadTime)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
  );

  return (
    <div className={classes.fileSelect}>
      {desktopGrid}
      {mobileGrid}
      <TablePagination
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={(event) => handleChangeRowsPerPage(event)}
        labelRowsPerPage={t("rowsPerPage")} // Localized label
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("of")} ${
            count !== -1 ? count : `${t("moreThan")} ${to}`
          }`
        }
      />
    </div>
  );
};

export default FileSelect;
